// What the report screens make of a report. No React here, so tests/admin
// can hold every figure to its source: Postgres is the truth and always
// answers — what was sent, what came back, who unsubscribed, what was bought;
// ClickHouse is the warehouse and answers when it can — who opened, who
// clicked. A number always says which of the two it is, and an absent
// warehouse is "nem elérhető", never a zero.

import type { AudienceEntry } from "@/lib/api";
import type { GroupReport, GroupTimelinePoint, PostgresFigures, VariantReport, WarehouseFigures } from "@/lib/tracking";
import type { Tone } from "@/components/emc/status";
import { labelOf } from "@/components/emc/status";
import { count } from "@/components/emc/format";
import { POOL_MIN_SAMPLE, judge, percentText, rateText, verdictText } from "@/components/emc/thresholds";

export type Source = "Postgres" | "ClickHouse";

export const UNAVAILABLE = "nem elérhető";

/** Euro cents as the marketer reads money: "1 234,56 €". */
export function euro(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return `${(cents / 100).toLocaleString("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Which ECB rate a euro figure was converted at, for its hover or footnote.
 * Every amount is converted at its own day's reference rate, so a sum of
 * several days names the span; nothing converted names nothing. */
export function fxNote(first: string | null | undefined, last: string | null | undefined): string {
  if (!first && !last) return "";
  if (!first || !last || first === last) return `EKB-árfolyam, ${first || last}`;
  return `EKB-árfolyam, ${first} – ${last} (mindegyik összeg a saját napjáé)`;
}

/** A Klaviyo campaign's audience as the marketer says it: the names, in
 * Klaviyo's order. A segment or list since deleted from the account has no
 * name left, so its id stands there, marked; one not looked up yet shows its
 * id until the next pull names it. Empty when there is none. */
export function audienceText(entries: readonly AudienceEntry[] | null | undefined): string {
  return (entries ?? [])
    .map((entry) => entry.name || (entry.kind === "missing" ? `${entry.id} (törölve)` : entry.id))
    .join(", ");
}

/** A per-thousand from the API ("ctr_permille": 143) as a percentage. */
export function permilleText(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return percentText(value / 10);
}

export interface Figure {
  key: string;
  label: string;
  value: string;
  /** What it is measured against: "az elküldöttek 98,7%-a". */
  comparison: string;
  /** The same in a few characters, for a card: "98,7%", "CTR 4,1%". */
  short: string;
  /** A second small line under the comparison, where a figure has one: the
   * opens' automatic share. */
  detail?: string;
  source: Source;
  /** False when the source did not answer: the value is "nem elérhető". */
  available: boolean;
  tone: Tone;
}

/** "ebből automatikus (Apple): 60, 19,4%" — the opens no person made, of all
 * of them. Unknown (a copied Klaviyo row) says nothing rather than zero. */
export function automaticText(machine: number | null | undefined, total: number | null | undefined): string {
  if (machine === null || machine === undefined) return "";
  const head = `ebből automatikus (Apple): ${count(machine)}`;
  return total !== null && total !== undefined && total > 0 ? `${head}, ${rateText(machine, total)}` : head;
}

function of(part: number, whole: number, what: string): string {
  return whole > 0 ? `${what} ${rateText(part, whole)}-a` : "nem ment ki levél";
}

/** The headline figures of a campaign (or a group, or a card), each with the
 * source it came from. `window` names the attribution window of the revenue. */
export function figures(
  postgres: PostgresFigures,
  warehouse: WarehouseFigures | null,
  window: string,
): Figure[] {
  const sent = postgres.sent;
  const bounce = judge("bounce", postgres.bounced, sent, POOL_MIN_SAMPLE);
  const complaint = judge("complaint", postgres.complained, sent, POOL_MIN_SAMPLE);
  const ctr = warehouse?.ctr_permille ?? null;
  return [
    {
      key: "sent",
      label: "Elküldve",
      value: count(sent),
      comparison: postgres.skipped > 0 ? `${count(postgres.skipped)} kihagyva (kizárás, higiénia)` : "a címzettek közül",
      short: "",
      source: "Postgres",
      available: true,
      tone: "muted",
    },
    {
      key: "delivered",
      label: "Kézbesítve",
      value: count(postgres.delivered),
      comparison: of(postgres.delivered, sent, "az elküldöttek"),
      short: rateText(postgres.delivered, sent),
      source: "Postgres",
      available: true,
      tone: "muted",
    },
    // Every open, Apple's automatic ones included, over the delivered — the
    // Klaviyo way (owner, 2026-10-24) — and under it how many were automatic.
    {
      key: "opens",
      label: "Egyedi nyitás",
      value: warehouse ? count(warehouse.opens_total) : UNAVAILABLE,
      comparison: warehouse
        ? postgres.delivered > 0
          ? `a kézbesítettek ${rateText(warehouse.opens_total, postgres.delivered)}-a`
          : "még nincs kézbesített levél"
        : "az eseménytárház nem válaszolt",
      detail: warehouse ? automaticText(warehouse.opens_machine, warehouse.opens_total) : "",
      short: warehouse ? rateText(warehouse.opens_total, postgres.delivered) : "",
      source: "ClickHouse",
      available: warehouse !== null,
      tone: "muted",
    },
    {
      key: "clicks",
      label: "Egyedi kattintás",
      value: warehouse ? count(warehouse.unique_clicks) : UNAVAILABLE,
      comparison: warehouse ? `CTR ${permilleText(ctr)} · emberi ${count(warehouse.unique_human_clicks)}` : "az eseménytárház nem válaszolt",
      short: warehouse ? `CTR ${permilleText(ctr)}` : "",
      source: "ClickHouse",
      available: warehouse !== null,
      tone: "muted",
    },
    {
      key: "unsubscribed",
      label: "Leiratkozás",
      value: count(postgres.unsubscribed),
      comparison: of(postgres.unsubscribed, sent, "az elküldöttek"),
      short: rateText(postgres.unsubscribed, sent),
      source: "Postgres",
      available: true,
      tone: "muted",
    },
    {
      key: "bounced",
      label: "Bounce",
      value: count(postgres.bounced),
      comparison: sent > 0 ? verdictText(bounce, POOL_MIN_SAMPLE) : "nem ment ki levél",
      short: rateText(postgres.bounced, sent),
      source: "Postgres",
      available: true,
      tone: bounce.tone,
    },
    {
      key: "complained",
      label: "Panasz",
      value: count(postgres.complained),
      comparison: sent > 0 ? verdictText(complaint, POOL_MIN_SAMPLE) : "nem ment ki levél",
      short: rateText(postgres.complained, sent),
      source: "Postgres",
      available: true,
      tone: complaint.tone,
    },
    {
      key: "revenue",
      label: "Bevétel",
      value: euro(postgres.revenue_eur_cents),
      comparison: [
        `${count(postgres.orders)} rendelés`,
        labelOf("attributionWindow", window),
        fxNote(postgres.fx_first_day, postgres.fx_last_day),
      ]
        .filter(Boolean)
        .join(" · "),
      short: `${count(postgres.orders)} rendelés`,
      source: "Postgres",
      available: true,
      tone: "muted",
    },
  ];
}

/** Every attribution window's orders and revenue, the headline one first.
 * Each names the ECB days of its own orders: a wider window holds later ones. */
export function windows(postgres: PostgresFigures, primary: string): { key: string; label: string; orders: string; revenue: string; fx: string }[] {
  const keys = Object.keys(postgres.by_window).sort((left, right) =>
    left === primary ? -1 : right === primary ? 1 : left.localeCompare(right),
  );
  return keys.map((key) => ({
    key,
    label: labelOf("attributionWindow", key),
    orders: count(postgres.by_window[key]?.orders ?? 0),
    revenue: euro(postgres.by_window[key]?.revenue_eur_cents ?? 0),
    fx: fxNote(postgres.by_window[key]?.fx_first_day, postgres.by_window[key]?.fx_last_day),
  }));
}

// --- the timeline -------------------------------------------------------------------

export interface CurveSeries {
  opens: { label: string; actual: number }[];
  clicks: { label: string; actual: number }[];
}

/** Opens and clicks per hour, labelled on the reader's clock: "23. 20:00" —
 * the day of the month and the hour, short enough for a 72-hour axis. */
export function timelineSeries(points: GroupTimelinePoint[], timeZone?: string): CurveSeries {
  const format = new Intl.DateTimeFormat("hu-HU", {
    timeZone,
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const labels = points.map((point) => {
    const parts = format.formatToParts(new Date(point.hour));
    const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
    return `${part("day")}. ${part("hour")}:${part("minute")}`;
  });
  return {
    opens: points.map((point, index) => ({ label: labels[index] ?? "", actual: point.opens })),
    clicks: points.map((point, index) => ({ label: labels[index] ?? "", actual: point.clicks })),
  };
}

// --- the arms ------------------------------------------------------------------------

export interface ArmRow {
  key: string;
  name: string;
  subject: string;
  share: string;
  winner: boolean;
  /** Where it won, when it did not win everywhere: "HU, SK". */
  wonIn: string;
  sent: string;
  delivered: string;
  bounce: string;
  bounceTone: Tone;
  complaint: string;
  complaintTone: Tone;
  unsubscribed: string;
  opens: string;
  clicks: string;
  revenue: string;
  orders: string;
}

export function armRows(variants: VariantReport[]): ArmRow[] {
  return variants.map((arm) => {
    const bounce = judge("bounce", arm.bounced, arm.sent, POOL_MIN_SAMPLE);
    const complaint = judge("complaint", arm.complained, arm.sent, POOL_MIN_SAMPLE);
    return {
      key: arm.key,
      name: arm.key === "rest" ? "maradék" : `„${arm.key}” változat`,
      subject: arm.subject,
      share: percentText(arm.share_permille / 10),
      winner: arm.is_winner,
      wonIn: arm.is_winner ? "" : (arm.won_in ?? []).join(", "),
      sent: count(arm.sent),
      delivered: rateText(arm.delivered, arm.sent),
      bounce: rateText(arm.bounced, arm.sent),
      bounceTone: bounce.tone,
      complaint: rateText(arm.complained, arm.sent),
      complaintTone: complaint.tone,
      unsubscribed: rateText(arm.unsubscribed, arm.sent),
      opens: rateText(arm.unique_human_opens, arm.sent),
      clicks: rateText(arm.unique_human_clicks, arm.sent),
      revenue: euro(arm.revenue_eur_cents),
      orders: count(arm.orders),
    };
  });
}

/** The warehouse's absence, in one sentence, when a report needed it — and
 * the failure's own line apart, for whoever has to fix it. */
export function warehouseNote(
  report: Pick<GroupReport, "source_unavailable" | "clickhouse_error">,
  shown: string = UNAVAILABLE,
): { text: string; reason: string | null } | null {
  if (!report.source_unavailable.includes("clickhouse")) return null;
  return {
    text: `A nyitás és a kattintás forrása, az eseménytárház (ClickHouse) most nem elérhető — a helyükön „${shown}” áll, nem nulla.`,
    reason: report.clickhouse_error,
  };
}
